/**
 * Determines whether a received event message corresponds to a stream output chunk.
 *
 * @param {string} data - The raw message data received from the stream.
 * @returns {boolean} `true` if the message contains the `[OUTPUT]` tag, otherwise `false`.
 *
 * @description
 * This function is used to identify messages that represent partial output events
 * in a Server-Sent Events (SSE) or streaming response.
 */
const isStreamingMessage = (data) => {
    return data.includes("[OUTPUT]")
}

/**
 * Determines whether a received event message indicates the end of a stream.
 *
 * @param {string} data - The raw message data received from the stream.
 * @returns {boolean} `true` if the message equals `[DONE]`, otherwise `false`.
 *
 * @description
 * Used to detect the termination signal in a Server-Sent Events (SSE) or streaming response.
 * When this message is detected, the connection should be closed and any cleanup logic executed.
 */
const isClosingMessage = (data) => {
    return data === "[DONE]"
}


/**
 * Opens a streaming connection to the application backend and handles message events.
 *
 * @param {string} params - URL parameters or path segment appended to the base API endpoint.
 * @param {(message: string) => void} onMessage - Callback executed for each streamed data chunk.
 * @param {(startTime: number, endTime: number) => void} onClose - Callback executed when the stream ends.
 * @returns {Promise<void>} A promise that resolves once the stream is completed, or rejects on error.
 *
 * @description
 * Establishes a Server-Sent Events (SSE) connection with the backend to handle real-time responses.
 * The function identifies data chunks using the `[OUTPUT]` marker and detects completion with the `[DONE]` tag.
 *
 * Example usage:
 * ```js
 * await streamAppAPI(
 *   "/generate/analysis",
 *   (chunk) => console.log("Received:", chunk),
 *   (start, end) => console.log(`Stream completed in ${end - start}ms`)
 * );
 * ```
 *
 * @throws {Error} If an error occurs during the stream or the connection fails.
 */
export const streamAppAPI = (params, onMessage, onClose) => {
    return new Promise(async (resolve, reject) => {
        const endpoint = process.env.REACT_APP_APP_API_URL + params
        const eventSource = new EventSource(endpoint);
        const start = Date.now()

        eventSource.onmessage = ({ data }) => {

            if (isStreamingMessage(data)) {
                const cleanData = data.replace("[OUTPUT]", "")
                onMessage(cleanData)
            }
            if (isClosingMessage(data)) {
                const end = Date.now()
                onClose(start, end)
                eventSource.close();
                resolve()
            }
        };

        eventSource.onerror = () => {
            eventSource.close();
            reject(new Error("Error while generating instrument analysis"))
        };
    })
}